import React, { useMemo } from "react";

import "./kit.css";

type T_ChartShimmerProps = {
  height?: number;
  width?: number;
};

const GRID_LINE_POSITIONS = ["25%", "45%", "65%", "85%"] as const;

/**
 * Premium chart loading shimmer — web mirror of the mobile ChartShimmer.
 * Sweeping gradient (CSS animation) over a subtle chart-line silhouette.
 */
export function ChartShimmer({
  height = 300,
  width = 353,
}: T_ChartShimmerProps) {
  const chartPath = useMemo(() => {
    const chartHeight = height * 0.6;
    const startY = chartHeight * 0.6;
    const points = [
      { x: 0, y: startY },
      { x: width * 0.15, y: startY * 0.95 },
      { x: width * 0.25, y: startY * 0.85 },
      { x: width * 0.35, y: startY * 0.9 },
      { x: width * 0.45, y: startY * 0.7 },
      { x: width * 0.55, y: startY * 0.75 },
      { x: width * 0.65, y: startY * 0.55 },
      { x: width * 0.75, y: startY * 0.6 },
      { x: width * 0.85, y: startY * 0.4 },
      { x: width, y: startY * 0.35 },
    ];
    const first = points[0];
    if (first === undefined) {
      throw new Error("Expected at least one point in chart shimmer");
    }
    let path = `M ${first.x} ${first.y}`;
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      if (prev === undefined || curr === undefined) {
        throw new Error(`Expected points at indices ${i - 1} and ${i}`);
      }
      const cpx = (prev.x + curr.x) / 2;
      path += ` Q ${cpx} ${prev.y} ${curr.x} ${curr.y}`;
    }
    return path;
  }, [height, width]);

  return (
    <div aria-busy className="lk-chart-shimmer" style={{ height }}>
      <div className="lk-chart-shimmer__base" />
      <div className="lk-chart-shimmer__sweep" />
      <div className="lk-chart-shimmer__silhouette">
        <svg
          fill="none"
          height={height * 0.6}
          role="presentation"
          width="100%"
          viewBox={`0 0 ${width} ${height * 0.6}`}
        >
          <path
            d={chartPath}
            stroke="var(--lattice-dark-grey-dark-4)"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      </div>
      {GRID_LINE_POSITIONS.map((top) => (
        <div
          className="lk-chart-shimmer__grid-line"
          key={top}
          style={{ top }}
        />
      ))}
    </div>
  );
}
